<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { useConvexAuth } from '../composables/useConvexAuth';

const router = useRouter();
const { isAuthenticated, isLoading: authLoading, signOut } = useConvexAuth();

// Get current user to display name
const currentUserQuery = useConvexQuery(api.auth.currentUser);
const currentUser = computed(() => currentUserQuery.data.value);
const isLoading = computed(() => authLoading.value || currentUserQuery.isPending.value);

const hasSession = computed(() => isAuthenticated.value);
const userName = computed(() => currentUser.value?.name);

const goToWorkshops = () => {
  router.push('/workshops');
};

const goToLogin = () => {
  router.push('/login');
};

const handleSignOut = async () => {
  await signOut();
  window.location.reload();
};

// Random rotation for the main card
const cardRotation = (Math.random() - 0.5) * 3;
</script>

<template>
  <div
    class="cork-board min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden"
  >
    <!-- Decorative scattered post-its -->
    <div class="scattered-postits"></div>

    <!-- Additional decorative elements -->
    <div
      class="absolute top-20 left-10 w-24 h-24 bg-(--color-postit-green) opacity-10 rotate-12 shadow-lg pointer-events-none"
    ></div>
    <div
      class="absolute bottom-32 right-16 w-20 h-20 bg-(--color-postit-orange) opacity-10 -rotate-8 shadow-lg pointer-events-none"
    ></div>
    <div
      class="absolute top-1/3 right-20 w-16 h-16 bg-(--color-postit-pink) opacity-10 rotate-20 shadow-lg pointer-events-none"
    ></div>

    <!-- Main pinned paper card -->
    <div
      class="paper-card cork-pin max-w-md w-full p-8 md:p-10 rounded-sm relative z-10"
      :style="{ '--card-rotation': cardRotation + 'deg' }"
    >
      <!-- Paper texture overlay -->
      <div class="paper-texture rounded-sm"></div>

      <!-- Content -->
      <div class="relative z-10">
        <div class="text-center mb-8">
          <h1 class="text-4xl md:text-5xl font-bold text-gray-800 mb-3 tracking-tight">
            {{ $t('landing.title') }}
          </h1>
          <p class="text-gray-600 text-lg">{{ $t('landing.subtitle') }}</p>
        </div>

        <div v-if="isLoading" class="text-center py-8">
          <div
            class="inline-block w-8 h-8 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mb-3"
          ></div>
          <p class="text-gray-500">{{ $t('common.loading') }}</p>
        </div>

        <div v-else-if="hasSession" class="space-y-5">
          <div class="text-center">
            <p class="text-gray-600 text-lg">
              {{ $t('landing.welcomeBack')
              }}<span v-if="userName"
                >, <strong class="text-gray-800">{{ userName }}</strong></span
              >!
            </p>
          </div>

          <div class="space-y-3 pt-2">
            <button @click="goToWorkshops" class="push-button w-full px-6 py-4 text-lg rounded-lg">
              {{ $t('landing.goToWorkshops') }}
            </button>
            <button
              @click="handleSignOut"
              class="push-button secondary w-full px-6 py-3 text-base rounded-lg"
            >
              {{ $t('auth.signOut') }}
            </button>
          </div>
        </div>

        <div v-else class="space-y-5">
          <p class="text-gray-500 text-center text-base">{{ $t('landing.signInToAccess') }}</p>

          <button @click="goToLogin" class="push-button w-full px-6 py-4 text-lg rounded-lg">
            {{ $t('auth.signIn') }}
          </button>

          <div class="text-center space-y-2 pt-2">
            <p class="text-gray-400 text-sm">{{ $t('landing.forReturningUsers') }}</p>
            <p class="text-gray-400 text-sm">{{ $t('landing.newHere') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
